import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import DefaultLayout from '@/layouts/Default/default';
import Image from '@/components/common/Image';
import Link from '@/components/common/Link';
import { blogPosts, getFeaturedPost } from '@/data/blogPosts';
import type { BlogPost } from '@/data/blogPosts';
import { Seo } from '@/components/SEO/Seo';
import { pageMetadata } from '@/config/pageMetadata';
import { siteMetadata } from '@/config/seo';
import { useSearchParams } from 'react-router-dom';

const CATEGORIES = [
  'All',
  'Agency',
  'Ventures',
  'Institute',
  'AI & Technology',
  'Insights',
];

const CATEGORY_SLUG_OVERRIDES: Record<string, string> = {
  ai: 'AI & Technology',
  'ai-technology': 'AI & Technology',
  'ai-and-technology': 'AI & Technology',
  insights: 'Insights',
  ventures: 'Ventures',
  agency: 'Agency',
  institute: 'Institute',
  all: 'All',
};

const categoryFromParam = (value: string | null): string => {
  if (!value) return 'All';
  const decoded = decodeURIComponent(value).replace(/\+/g, ' ').trim();
  if (!decoded) return 'All';

  const normalized = decoded.toLowerCase();
  if (CATEGORY_SLUG_OVERRIDES[normalized]) {
    return CATEGORY_SLUG_OVERRIDES[normalized];
  }

  const directMatch = CATEGORIES.find((category) => category.toLowerCase() === normalized);
  if (directMatch) return directMatch;

  const loosened = CATEGORIES.find((category) =>
    category
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/\s+/g, '-') === normalized
  );

  return loosened ?? 'All';
};

const categoryToParam = (category: string): string | null => {
  if (category === 'All') return null;
  return category
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\s+/g, '-');
};

const BlogPage: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(() =>
    categoryFromParam(searchParams.get('category'))
  );
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [loadedPosts, setLoadedPosts] = useState<Record<string, boolean>>({});

  const featuredPost = useMemo(() => getFeaturedPost(), []);

  useEffect(() => {
    const currentParamCategory = categoryFromParam(searchParams.get('category'));
    setActiveCategory((prev) => (prev === currentParamCategory ? prev : currentParamCategory));
  }, [searchParams]);

  const handleCategorySelect = useCallback(
    (category: string) => {
      setActiveCategory(category);
      const paramValue = categoryToParam(category);
      const nextParams = new URLSearchParams(searchParams);

      if (paramValue) {
        nextParams.set('category', paramValue);
      } else {
        nextParams.delete('category');
      }

      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const filteredPosts = useMemo<BlogPost[]>(() => {
    if (activeCategory === 'All') return blogPosts;
    return blogPosts.filter((post) => post.category === activeCategory);
  }, [activeCategory]);

  const seo = pageMetadata.blog;
  const blogStructuredData = useMemo(() => {
    const canonicalUrl = `${siteMetadata.siteUrl}${seo.canonicalPath ?? '/blog'}`;
    const toAbsoluteImage = (src: string) => {
      if (!src) return undefined;
      if (/^https?:\/\//i.test(src)) return src;
      const normalized = src.startsWith('/') ? src : `/${src}`;
      return `${siteMetadata.siteUrl}${normalized}`;
    };
    const posts = blogPosts.map((post) => {
      const isoDate = (() => {
        if (!post.date) return undefined;
        const parsed = new Date(post.date);
        return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
      })();
      return {
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt,
        url: `${siteMetadata.siteUrl}/blog/${post.slug}`,
        datePublished: isoDate,
        image: toAbsoluteImage(post.imageUrl),
        author: {
          '@type': 'Person',
          name: post.author.name,
        },
      };
    });
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: `${siteMetadata.siteName} Blog`,
        description: seo.description ?? siteMetadata.defaultDescription,
        url: canonicalUrl,
        blogPost: posts,
      },
    ];
  }, [blogPosts, seo.canonicalPath, seo.description]);

  return (
    <DefaultLayout hideHeader>
      <Seo {...seo} structuredData={blogStructuredData} />
      <div className="min-h-screen bg-white dark:bg-zinc-950">
        <section className="container mx-auto max-w-[97%] px-4 pt-24">
          <Link
            href={`/blog/${featuredPost.slug}`}
            className="relative block h-[500px] overflow-hidden rounded-3xl shadow-lg"
          >
            <div className="relative h-full w-full">
              {!heroLoaded && (
                <div className="absolute inset-0 animate-pulse rounded-3xl bg-zinc-200/70 dark:bg-zinc-800/60" />
              )}
              <Image
                src={featuredPost.imageUrl}
                alt={featuredPost.title}
                fill
                className={`object-cover transition-opacity duration-500 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setHeroLoaded(true)}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
            </div>

            <div className="absolute inset-0 flex flex-col justify-end p-10 pb-16">
              <div className="flex">
                <span className="mb-3 inline-block rounded-full bg-black/30 px-4 py-1.5 text-sm text-white backdrop-blur-sm">
                  {featuredPost.category}
                </span>
              </div>
              <h1 className="mb-3 text-4xl font-medium text-white">
                {featuredPost.title}
              </h1>
              <p className="mb-6 max-w-3xl text-lg text-white/90">
                {featuredPost.excerpt}
              </p>

              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-6 text-white/80 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 overflow-hidden rounded-full">
                      <Image
                        src={featuredPost.author.imageUrl}
                        alt={featuredPost.author.name}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <span className="text-white text-sm">
                      {featuredPost.author.name}
                    </span>
                  </div>
                  <span>
                    {featuredPost.date} • {featuredPost.readTime}
                  </span>
                </div>
                <span className="flex items-center rounded-full bg-white/20 px-5 py-2 text-sm font-medium text-white backdrop-blur-sm transition-colors duration-300 hover:bg-white/30">
                  View Article
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="ml-2 h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </span>
              </div>
            </div>
          </Link>
        </section>

        <div className="container mx-auto mb-20 mt-16 px-4">
          <h2 className="mb-2 text-2xl text-zinc-900 dark:text-white">
            Insights & Resources
          </h2>
          <p className="mb-8 text-zinc-600 dark:text-zinc-400">
            Explore our latest thinking on technology, AI integration, and
            talent development in the modern tech landscape.
          </p>

          <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => handleCategorySelect(category)}
                  className={`rounded-lg px-4 py-2 text-sm transition-colors ${
                    activeCategory === category
                      ? 'bg-zinc-800 text-white dark:bg-white dark:text-zinc-900'
                      : 'bg-zinc-100 text-zinc-800 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-zinc-600 dark:border-zinc-700 dark:text-zinc-400">
              <span>Sort by:</span>
              <span className="font-medium text-zinc-900 dark:text-white">Newest</span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredPosts.map((post) => (
              <Link
                key={post.id}
                href={`/blog/${post.slug}`}
                className="flex h-full flex-col overflow-hidden rounded-lg border border-zinc-100 transition-shadow duration-300 hover:border-zinc-300 hover:shadow-md dark:border-zinc-800 dark:hover:border-zinc-700"
              >
                <div className="relative h-56">
                  {!loadedPosts[post.id] && (
                    <div className="absolute inset-0 animate-pulse bg-zinc-200/70 dark:bg-zinc-800/60" />
                  )}
                  <Image
                    src={post.imageUrl}
                    alt={post.title}
                    fill
                    className={`object-cover transition-opacity duration-500 ${loadedPosts[post.id] ? 'opacity-100' : 'opacity-0'}`}
                    onLoad={() =>
                      setLoadedPosts((prev) => ({ ...prev, [post.id]: true }))
                    }
                  />
                  <span className="absolute top-4 left-4 rounded-full bg-zinc-700/70 px-3 py-1 text-xs text-white backdrop-blur-sm">
                    {post.category}
                  </span>
                </div>

                <div className="flex h-full flex-col p-4">
                  <div className="mb-2 text-xs text-zinc-500 dark:text-zinc-400">
                    {post.date} • {post.readTime}
                  </div>
                  <h3 className="mb-2 text-xl text-zinc-900 dark:text-white">
                    {post.title}
                  </h3>
                  <p className="mb-4 text-sm text-zinc-600 dark:text-zinc-400">
                    {post.excerpt}
                  </p>

                  <div className="mt-auto flex items-center">
                    <div className="h-8 w-8 overflow-hidden rounded-full">
                      <Image
                        src={post.author.imageUrl}
                        alt={post.author.name}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <span className="ml-2 text-sm text-zinc-900 dark:text-zinc-300">
                      {post.author.name}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default BlogPage;
